import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Drawer, btnGhost, btnPrimary } from "@/pages/admin/components/AdminUI";
import HelpFaqList from "@/pages/admin/components/HelpFaqList";
import { adminHelpTopics, platformSupport } from "@/pages/admin/helpContent";

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-in help panel opened from the top-bar Help button. Searchable quick
 * answers grouped by topic, with shortcuts into the relevant modules and a link
 * out to platform support.
 */
export default function HelpDrawer({ open, onClose }: HelpDrawerProps) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const topics = useMemo(() => {
    if (!q) return adminHelpTopics;
    return adminHelpTopics
      .map((topic) => ({
        ...topic,
        faqs: topic.faqs.filter((faq) =>
          `${faq.q} ${faq.a} ${faq.toLabel || ""}`.toLowerCase().includes(q),
        ),
      }))
      .filter((topic) => topic.faqs.length > 0);
  }, [q]);

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title="Help & Quick Answers"
      subtitle="Admin portal"
      footer={
        <div className="flex flex-col gap-2">
          <Link to="/admin/help" onClick={handleClose} className={`${btnPrimary} justify-center`}>
            <i className="ri-book-open-line"></i>
            Open the full help centre
          </Link>
          <a
            href={platformSupport.docs}
            target="_blank"
            rel="noopener noreferrer"
            className={`${btnGhost} justify-center`}
          >
            <i className="ri-external-link-line"></i>
            {platformSupport.docsLabel}
          </a>
        </div>
      }
    >
      <div className="relative">
        <span className="w-5 h-5 flex items-center justify-center absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400">
          <i className="ri-search-line text-sm"></i>
        </span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search help — e.g. approve, refund, export…"
          className="w-full bg-background-100 border border-background-300 rounded-md pl-11 pr-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
        />
      </div>

      {topics.length === 0 ? (
        <div className="py-10 text-center">
          <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-500">
            <i className="ri-search-eye-line text-2xl"></i>
          </span>
          <p className="text-sm text-foreground-600 mt-4">
            No quick answers match “{query}”. Try another word, or open the full help centre.
          </p>
        </div>
      ) : (
        topics.map((topic) => (
          <div key={topic.id}>
            <div className="flex items-center gap-3 mb-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-900 shrink-0">
                <i className={`${topic.icon} text-lg`}></i>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground-950">{topic.title}</p>
                <p className="text-xs text-foreground-500">{topic.blurb}</p>
              </div>
            </div>
            <HelpFaqList faqs={topic.faqs} onNavigate={handleClose} />
          </div>
        ))
      )}
    </Drawer>
  );
}